import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const heroImages = [
  'https://readdy.ai/api/search-image?query=advanced%20polymer%20material%20laboratory%20with%20green%20technology%20circular%20economy%20recycled%20plastic%20pellets%20industrial%20process%20clean%20modern%20facility%20with%20soft%20green%20and%20blue%20lighting%20professional%20B2B%20industrial%20photography%20high%20resolution%20wide%20angle&width=1440&height=780&seq=hero001&orientation=landscape',
  'https://readdy.ai/api/search-image?query=close-up%20of%20colorful%20recycled%20plastic%20pellets%20granules%20with%20molecular%20structure%20visualization%20green%20and%20blue%20technology%20glow%20circular%20arrows%20sustainable%20materials%20laboratory%20environment%20clean%20industrial%20background%20with%20soft%20bokeh%20professional%20B2B%20photography%20high%20resolution&width=1440&height=780&seq=hero-home-v2&orientation=landscape',
];

const INTERVAL = 5000;
const FADE_DURATION = 900;

export default function HomeHero() {
  const { lang } = useLanguage();
  const [activeIdx, setActiveIdx] = useState(0);
  const [nextIdx, setNextIdx] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  const goTo = useCallback(
    (idx: number) => {
      if (transitioning || idx === activeIdx) return;
      setNextIdx(idx);
      setTransitioning(true);
      setTimeout(() => {
        setActiveIdx(idx);
        setNextIdx(null);
        setTransitioning(false);
      }, FADE_DURATION);
    },
    [activeIdx, transitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => { goTo((activeIdx + 1) % heroImages.length); }, INTERVAL);
    return () => clearInterval(timer);
  }, [activeIdx, goTo]);

  const badge = lang === 'en' ? 'Functional Materials · Circular Economy Technology'
    : lang === 'ja' ? '機能性材料・サーキュラーエコノミー技術'
    : '功能型材料 · 循環經濟技術';

  const h1 = lang === 'en' ? (
    <>Nano-Material Technology<br />That Transforms Waste Plastics into{' '}
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        High-Value Circular Materials
      </span>
    </>
  ) : lang === 'ja' ? (
    <>ナノ材料技術で廃プラスチックを<br />
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        高付加価値の循環材料へ
      </span>
    </>
  ) : (
    <>以奈米材料科技<br />讓廢棄塑膠重回<br className="sm:hidden" />
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        高價值原料
      </span>
    </>
  );

  const subtitle = lang === 'en'
    ? 'PolyQ Applied Materials leverages PQernel® nano-scale polymer chain extender and PQMB masterbatch technology to help plastic recyclers, compounders, injection/extrusion manufacturers, and brand owners upgrade waste and off-grade plastics into stable, production-ready recycled materials — enabling circular economy and low-carbon manufacturing.'
    : lang === 'ja'
    ? '聚量応用材料は、PQernel®ナノ高分子鎖延長補強剤とPQMBマスターバッチ技術を活用し、廃プラスチックリサイクル業者・造粒業者・射出押出メーカー・ブランドオーナーが廃棄・低品位プラスチックを安定した再生材料へとアップグレードできるよう支援します。サーキュラーエコノミーと低炭素製造を実現します。'
    : '聚量應用材料以 PQernel® 奈米級高分子擴鏈補強劑與 PQMB 母粒技術，協助塑膠回收廠、造粒廠、射出押出製造廠與品牌端，將廢棄與次級塑料升級為可穩定再投入產線的再生材料，實現循環經濟與低碳製造。';

  const cta1 = lang === 'en' ? 'Explore Core Technology' : lang === 'ja' ? 'コア技術を見る' : '了解核心技術';
  const cta2 = lang === 'en' ? 'Schedule a Consultation' : lang === 'ja' ? '技術相談を予約する' : '預約技術諮詢';

  const tags = lang === 'en'
    ? [{ icon: 'ri-flask-line', label: 'PQernel® Chain Extender' }, { icon: 'ri-capsule-line', label: 'PQMB Masterbatch' }, { icon: 'ri-recycle-line', label: 'Plastics Reviving' }, { icon: 'ri-leaf-line', label: 'Circular Economy & Net-Zero' }]
    : lang === 'ja'
    ? [{ icon: 'ri-flask-line', label: 'PQernel® 鎖延長補強剤' }, { icon: 'ri-capsule-line', label: 'PQMBマスターバッチ' }, { icon: 'ri-recycle-line', label: 'Plastics Reviving' }, { icon: 'ri-leaf-line', label: 'サーキュラーエコノミー・ネットゼロ' }]
    : [{ icon: 'ri-flask-line', label: 'PQernel® 奈米擴鏈補強劑' }, { icon: 'ri-capsule-line', label: 'PQMB 母粒' }, { icon: 'ri-recycle-line', label: 'Plastics Reviving' }, { icon: 'ri-leaf-line', label: '循環經濟與淨零碳排' }];

  const dotLabel = (idx: number) => lang === 'en' ? `Go to slide ${idx + 1}` : lang === 'ja' ? `スライド ${idx + 1} へ` : `切換至第 ${idx + 1} 張圖片`;

  return (
    <section className="relative min-h-[680px] md:min-h-[780px] flex items-center overflow-hidden">
      {heroImages.map((src, idx) => {
        const isActive = idx === activeIdx;
        const isNext = idx === nextIdx;
        let opacity = 0;
        if (isActive) opacity = transitioning ? 0 : 1;
        if (isNext) opacity = transitioning ? 1 : 0;
        return (
          <div key={src} className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: `url(${src})`, opacity, transition: `opacity ${FADE_DURATION}ms ease-in-out`, zIndex: isNext ? 2 : isActive ? 1 : 0 }} aria-hidden="true" />
        );
      })}
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/22 to-black/5" style={{ zIndex: 3 }} />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/10 to-transparent" style={{ zIndex: 3 }} />
      <div className="absolute top-0 right-0 w-[700px] h-[700px] opacity-25 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,184,148,0.30) 0%, transparent 65%)', zIndex: 3 }} aria-hidden="true" />
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[400px] opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.25) 0%, transparent 65%)', zIndex: 3 }} aria-hidden="true" />

      <div className="relative w-full max-w-screen-xl mx-auto px-4 md:px-6 py-20" style={{ zIndex: 4 }}>
        <div className="max-w-[680px]">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-green/20 border border-brand-green/50 mb-7">
            <i className="ri-leaf-line text-brand-green text-sm"></i>
            <span className="text-brand-green text-[1rem] font-semibold tracking-wide">{badge}</span>
          </div>
          <h1 className="font-bold text-white mb-6" style={{ fontSize: 'clamp(2rem, 4.5vw, 2.75rem)', lineHeight: '1.3' }}>{h1}</h1>
          <p className="text-white/85 mb-8 max-w-[600px]" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{subtitle}</p>
          <div className="flex flex-wrap gap-4 mb-10">
            <Link to="/technology" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-flask-line"></i>{cta1}
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/15 border border-white/50 text-white text-[1.0625rem] font-semibold hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-calendar-schedule-line"></i>{cta2}
            </Link>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {tags.map((tag) => (
              <div key={tag.label} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm">
                <i className={`${tag.icon} text-brand-green text-sm`}></i>
                <span className="text-white/90 text-[0.9375rem] font-medium whitespace-nowrap">{tag.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex items-center gap-2.5" style={{ zIndex: 5 }}>
        {heroImages.map((_, idx) => (
          <button key={idx} onClick={() => goTo(idx)} aria-label={dotLabel(idx)} className="cursor-pointer transition-all duration-300"
            style={{ width: idx === activeIdx ? '28px' : '8px', height: '8px', borderRadius: '4px', background: idx === activeIdx ? '#00B894' : 'rgba(255,255,255,0.5)', border: 'none', padding: 0 }} />
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ zIndex: 5, background: 'rgba(255,255,255,0.15)' }}>
        <div key={activeIdx} className="h-full bg-brand-green" style={{ animation: `heroProgress ${INTERVAL}ms linear forwards` }} />
      </div>
      <style>{`@keyframes heroProgress { from { width: 0%; } to { width: 100%; } }`}</style>
    </section>
  );
}
